import { ButtonType, CompactInlineAlertWidget, DropdownButtonType, InlineAlertType, List, SingleSelectDropdown, TextLinkWidget } from '@tableau/widgets';
import React from 'react';
import _ from 'underscore';

import { ButtonWithSpinner } from '~commonInfra/components/ButtonWithSpinner';
import { HelpService } from '~commonInfra/services/HelpService';
import { Set } from '~commonInfra/utils/Set';

import { Messages } from '../../codegen/Localize';
import { EmailValidationHelper } from '../helpers/EmailValidationHelper';
import { UserActionNotification } from '../services/notifications/UserActionNotification';
import { SiteUsers } from '../services/resources/SiteUsers';
import { IUserActionResult, UserActionResultCode } from '../users/serverApi/types/IUserActionResult';
import { getIntegrationSiteRoleItems } from './IntegrationSiteRoleListItems';
import { MultilineTextField } from './MultilineTextField';
import {
  AddIntegrationUsersDialogContainerStyle,
  DialogDescriptionStyle,
  DialogFieldLabelStyle,
  DialogSectionStyle
} from './styles/IntegrationUserDialogStyles';

export interface AddIntegrationUsersDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
}

export interface AddIntegrationUsersDialogState {
  readonly userInput: string;
  readonly siteRole: string | undefined;
  readonly busy: boolean;
  readonly invalidEmails: string[];
}

const INTEGRATION_USERS_HELP_TOKEN = 'integration-users';

const splitUsernames = (input: string): string[] => _.chain(input.split(/[;,\r\n]+/))
                                                    .map((name: string) => name.trim())
                                                    .compact()
                                                    .unique()
                                                    .value();

export class AddIntegrationUsersDialog extends React.Component<AddIntegrationUsersDialogProps, AddIntegrationUsersDialogState> {

  public constructor(props: AddIntegrationUsersDialogProps) {
    super(props);
    this.state = { userInput: '', siteRole: undefined, busy: false, invalidEmails: [] };
  }

  private readonly handleUsernamesChange = (userInput: string) => {
    this.setState({ userInput });
  };

  private readonly handleSiteRoleChange = (value: string) => {
    this.setState({ siteRole: value });
  };

  private readonly handleSubmit = () => {
    const usernames = splitUsernames(this.state.userInput);
    if (usernames.length === 0 || this.state.siteRole == null) {
      return;
    }
    const invalidEmails = EmailValidationHelper.getInvalidEmails(usernames);
    this.setState({ invalidEmails });
    if (invalidEmails.length > 0) {
      return;
    }
    this.setState({ busy: true });
    SiteUsers.instance.importIntegrationUsers(usernames, this.state.siteRole)
      .then((result: IUserActionResult) => {
        UserActionNotification.instance.notify({
          users: Set.fromIdItemArray(result.users),
          results: result.results,
          errors: result.errors,
          successCode: UserActionResultCode.siteUserCreated,
          siteRole: this.state.siteRole,
        });
        this.props.close(result);
      })
      .catch(() => this.setState({ busy: false }));
  };

  private renderInvalidEmailsIfApplicable() {
    if (this.state.invalidEmails && this.state.invalidEmails.length > 0) {
      const translatedInvalidEmails = this.state.invalidEmails.map((invalidEmail: string) => {
        return Messages.addExternalUsersInvalidEmail({ EMAIL: invalidEmail });
      });
      return (
        <div className='tb-dialog-warnings-list'>
          {translatedInvalidEmails.map((translatedInvalidEmail: string, index) => (
            <CompactInlineAlertWidget
              key={index}
              testId={`invalid-email-alert-${index}`}
              type={InlineAlertType.Warning}
            >
              {translatedInvalidEmail}
            </CompactInlineAlertWidget>
          ))}
        </div>
      );
    }
    return null;
  }

  public render() {
    const isSubmitEnabled = !this.state.busy
      && this.state.userInput.trim().length > 0
      && this.state.siteRole != null;
    const siteRoleButtonLabel = this.state.siteRole == null
      ? Messages.addIntegrationUsersSiteRolePlaceholder()
      : undefined;
    return (
      <div className='tb-dialog-integration-users' style={AddIntegrationUsersDialogContainerStyle}>
        <div className='tb-dialog-title'>{Messages.addIntegrationUsers()}</div>
        <div style={DialogDescriptionStyle}>
          {Messages.addIntegrationUsersDescription()}{' '}
          <TextLinkWidget
            href={HelpService.getServerHelpUrlByCategory(INTEGRATION_USERS_HELP_TOKEN)}
            target='_blank'
            testId='integration-users-learn-more'
          >
            {Messages.learnMore()}
          </TextLinkWidget>
        </div>
        <div style={DialogSectionStyle}>
          <div style={DialogFieldLabelStyle}>{Messages.addIntegrationUsersEnterUsernamesLabel()}</div>
          <div className='tb-dialog-body-control tb-multiline-text-holder'>
            <MultilineTextField
              handleChange={this.handleUsernamesChange}
              disabled={false}
              placeholder={Messages.addIntegrationUsersPlaceholder()}
              testId='integration-users-usernames'
            />
          </div>
          {this.renderInvalidEmailsIfApplicable()}
        </div>
        <div style={DialogSectionStyle}>
          <div style={DialogFieldLabelStyle}>{Messages.siteRole_title()}</div>
          <SingleSelectDropdown
            list={<List items={getIntegrationSiteRoleItems()} />}
            value={this.state.siteRole}
            onChange={this.handleSiteRoleChange}
            type={DropdownButtonType.Outline}
            label=''
            aria-label={Messages.siteRole_title()}
            testId='integration-users-site-role-dropdown'
          >
            {siteRoleButtonLabel}
          </SingleSelectDropdown>
        </div>
        <div className='tb-dialog-actions'>
          <ButtonWithSpinner
            buttonType={ButtonType.Outline}
            onClick={this.props.dismiss}
            testId='confirm-action-dialog-cancel'
            children={Messages.cancel()}
            disabled={this.state.busy}
          />
          <span className='tb-margin-left-wide'>
            <ButtonWithSpinner
              buttonType={ButtonType.Primary}
              onClick={this.handleSubmit}
              testId='confirm-action-dialog-submit'
              children={Messages.addIntegrationUsers()}
              disabled={!isSubmitEnabled}
              showSpinner={this.state.busy}
            />
          </span>
        </div>
      </div>
    );
  }
}
